import { useEffect, useMemo, useState } from 'react';

/**
 * usePagination — client-side slicing of an in-memory row set.
 *
 * Extracted from the Usage page's "Recent executions" table so the same
 * paginated UX can be reused (e.g. the Billing "Recent activity" table).
 * The page owns the data + (optional) filtering; this hook owns the page
 * index, clamping, and the visible slice. Pair it with the <Pagination>
 * atom for the footer controls.
 *
 * Params:
 *   rows     — the full (already-filtered) array to paginate
 *   pageSize — rows per page (default 25, matching the Usage page)
 *   deps     — when any value here changes, reset to page 1 (e.g. the
 *              active filter or window changed, so the row set differs)
 *
 * Returns:
 *   page        — clamped current page (1-based)
 *   setPage     — setter accepting a number or updater fn
 *   pageCount   — total pages (>= 1)
 *   total       — total row count
 *   pageSize    — echoed back for the footer's range math
 *   pagedRows   — the visible slice for this page
 */
const usePagination = (rows, pageSize = 25, deps = []) => {
  const [page, setPage] = useState(1);
  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const total = safeRows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const page_ = Math.min(page, pageCount);

  // Reset to page 1 when the underlying row set changes (filter / window).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, deps);

  const pagedRows = useMemo(
    () => safeRows.slice((page_ - 1) * pageSize, page_ * pageSize),
    [safeRows, page_, pageSize],
  );

  return { page: page_, setPage, pageCount, total, pageSize, pagedRows };
};

export default usePagination;
